import * as MsDyn365 from '@msdyn365-commerce/core';
import React from 'react';
import { IActionsData, ILxsQuickActionsConfig } from './lxs-quick-actions.props.autogenerated';

const CLASS_NAME = 'lxs-quick-actions';

interface ILxsQuickActionsViewProps {
    config: ILxsQuickActionsConfig;
    requestContext: MsDyn365.IRequestContext;
    onHeadingEdit: (actionIndex: number, value: string) => void;
    onDescriptionEdit: (actionIndex: number, value: string) => void;
}

const renderHeading = (
    heading: IActionsData['heading'],
    actionIndex: number,
    requestContext: MsDyn365.IRequestContext,
    onHeadingEdit: (actionIndex: number, value: string) => void
) => {
    if (!heading?.text) {
        return null;
    }

    return (
        <MsDyn365.Text
            className={`${CLASS_NAME}__card-heading`}
            tag={heading.tag ?? 'h3'}
            text={heading.text}
            editProps={{ onEdit: event => onHeadingEdit(actionIndex, event.target.value), requestContext }}
        />
    );
};

const QuickActionCard: React.FC<{
    action: IActionsData;
    actionIndex: number;
    requestContext: MsDyn365.IRequestContext;
    onHeadingEdit: (actionIndex: number, value: string) => void;
    onDescriptionEdit: (actionIndex: number, value: string) => void;
}> = ({ action, actionIndex, requestContext, onHeadingEdit, onDescriptionEdit }) => {
    const { icon, heading, description, link } = action;
    const destinationUrl = link?.linkUrl?.destinationUrl;
    const href = destinationUrl ?? undefined;
    const target = link?.openInNewTab ? '_blank' : undefined;
    const rel = link?.openInNewTab ? 'noopener noreferrer' : undefined;
    const isCardClickable = Boolean(destinationUrl);
    const cardClassName = `${CLASS_NAME}__card${isCardClickable ? ` ${CLASS_NAME}__card--clickable` : ''}`;

    const cardContent = (
        <>
            <div className={`${CLASS_NAME}__card-header`}>
                {icon && <span className={`${CLASS_NAME}__icon ${CLASS_NAME}__icon--${icon}`} aria-hidden='true' />}
                {renderHeading(heading, actionIndex, requestContext, onHeadingEdit)}
            </div>
            {description && (
                <div className={`${CLASS_NAME}__card-description`}>
                    <MsDyn365.RichTextComponent
                        text={description}
                        editProps={{ onEdit: event => onDescriptionEdit(actionIndex, event.target.value), requestContext }}
                    />
                </div>
            )}
            {link?.linkText && <span className={`${CLASS_NAME}__card-link`}>{link.linkText}</span>}
        </>
    );

    if (isCardClickable && href) {
        return (
            <a className={cardClassName} href={href} target={target} rel={rel} aria-label={link?.ariaLabel ?? link?.linkText}>
                {cardContent}
            </a>
        );
    }

    return <div className={cardClassName}>{cardContent}</div>;
};

const LxsQuickActionsView: React.FC<ILxsQuickActionsViewProps> = ({ config, requestContext, onHeadingEdit, onDescriptionEdit }) => {
    const { actions, className } = config;
    const { enableCardAnimation = true } = config as ILxsQuickActionsConfig & { enableCardAnimation?: boolean };
    const rootClassName = `${CLASS_NAME} ${enableCardAnimation ? `${CLASS_NAME}--animated` : ''} ${className ? `${className}` : ''}`;

    if (!actions?.length) {
        return null;
    }

    return (
        <div className={rootClassName}>
            <div className={`${CLASS_NAME}__grid`}>
                {actions.map((action, index) => (
                    <QuickActionCard
                        key={index}
                        action={action}
                        actionIndex={index}
                        requestContext={requestContext}
                        onHeadingEdit={onHeadingEdit}
                        onDescriptionEdit={onDescriptionEdit}
                    />
                ))}
            </div>
        </div>
    );
};

export default LxsQuickActionsView;
